import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Flex, Spinner } from 'components';
import { GET_CASE_COMMENTS } from 'connectors/queries/rmaCases';
import { useRecoilState, useRecoilValue } from 'recoil';
import { authenticationState, caseAssetsState } from 'state/atoms';
import { useNotifyError } from 'utils/hooks';
import { CommentBody, CommentText, CommentWrapper, SpinnerWrapper } from './styledComponents';
import { transformLineBreakToBR } from 'utils/helpers/string';
import { timeSince, timeFormatter, dateFormatter } from 'utils/helpers/date';
import LinkedAttachments from './subcomponents/LinkedAttachments';
import { NoAssetsMessage } from '../../styledComponents';

const readComments = (data) => data?.getAccount?.getRmaCaseByNumber?.getComments;

const getQueryVariables = ({ accountId, caseId, caseNumber, offset }) => {
  return {
    accountId,
    caseId,
    offset,
    input: {
      accountId,
      caseNumber,
    },
  };
};

const updateResults = (prevData, { fetchMoreResult }) => {
  const prevResults = readComments(prevData)?.comments || [];
  const moreResults = readComments(fetchMoreResult)?.comments || [];

  return {
    ...fetchMoreResult,
    getAccount: {
      ...fetchMoreResult.getAccount,
      getRmaCaseByNumber: {
        ...fetchMoreResult.getAccount.getRmaCaseByNumber,
        getComments: {
          ...fetchMoreResult.getAccount.getRmaCaseByNumber.getComments,
          comments: [...prevResults, ...moreResults],
        },
      },
    },
  };
};

const Comments = ({ caseId, caseNumber }) => {
  const [assets, setAssets] = useRecoilState(caseAssetsState);
  const { attachments = [], comments = [], relationships = [] } = assets;

  const linkedAttachments = useMemo(() => {
    return attachments.reduce((link, attachment) => {
      const relationship = relationships.find(
        (relationship) => relationship.attachmentId === attachment.id
      );

      if (!relationship) {
        return link;
      }

      if (!link[relationship.commentId]) {
        link[relationship.commentId] = [];
      }

      link[relationship.commentId].push(attachment);

      return link;
    }, {});
  }, [attachments, relationships]);

  const { endCustomerAccountId: accountId } = useRecoilValue(authenticationState);

  const notifyError = useNotifyError({
    fallbackMessage: 'Unable to fetch comments.',
  });

  // Fetch policy is set to cache-first to avoid refetching the comments for the RMA Case
  // This is a stop-gap fix until we can refactor the code to use a custom hook to replace useQuery
  // This custom hook will handle the loading state and prevent unnecessary re-renders on subsequent paginated fetches
  const { data, loading, fetchMore } = useQuery(GET_CASE_COMMENTS, {
    variables: getQueryVariables({
      accountId,
      caseId,
      caseNumber,
    }),
    fetchPolicy: 'cache-first',
    onError: notifyError,
  });

  useEffect(() => {
    if (loading) {
      return;
    }

    const response = data && readComments(data);

    if (response && response.hasMore) {
      fetchMore({
        variables: getQueryVariables({
          accountId,
          caseNumber,
          caseId,
          offset: response.comments.length,
        }),
        updateQuery: updateResults,
        notifyOnNetworkStatusChange: true,
      });
    } else {
      setAssets({ ...assets, comments: response ? response.comments : [] });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, caseId, caseNumber, data, loading, fetchMore]);

  if (loading) {
    return (
      <SpinnerWrapper spacing="center">
        <Spinner />
      </SpinnerWrapper>
    );
  }

  if (!loading && !comments.length) {
    return <NoAssetsMessage>This case does not have comments.</NoAssetsMessage>;
  }

  return comments.map((commentDetails) => {
    const date = new Date(commentDetails.createdAt);
    const time = date.getTime();
    const formattedDate = dateFormatter.format(date);
    const formattedTime = timeFormatter.format(date);
    const { interval, unit } = timeSince(time);

    return (
      <CommentWrapper key={commentDetails.id}>
        <Flex>
          <CommentText strong>{commentDetails.createdBy.name}</CommentText>
          <CommentText title={`${formattedDate} ${formattedTime}`}>
            {`${interval} ${unit} ago`}
          </CommentText>
        </Flex>
        <CommentBody
          dangerouslySetInnerHTML={{
            __html: transformLineBreakToBR(commentDetails.comment),
          }}
        />
        <LinkedAttachments linkedAttachments={linkedAttachments[commentDetails.id]} />
      </CommentWrapper>
    );
  });
};
export default Comments;
